// ─── DARK POOL REPLAY — price action + institutional flow overlay ────────────
// Reconstructs one trading session from the archived stream history:
//   GET /api/replay/{symbol}?date=YYYY-MM-DD
//     price_bars      — raw Alpaca 1-min SIP bars (short keys: t/o/h/l/c/v)
//     dark_prints     — archived dark-pool prints, oldest-first
//     unusual_options — archived unusual-options signals, oldest-first
//
// Charting is Recharts only. The price panel is a ComposedChart carrying real
// candlesticks (a custom Bar shape over the [low, high] range) plus a Scatter
// overlay of dark-pool prints — one coordinate system, so the dots align with
// the candles structurally instead of being pixel-synced to a second canvas.
// The options lane below is a separate chart sharing the SAME numeric epoch-ms
// X domain, so the two panels stay time-aligned.
//
// ⚠ The options lane is a ComposedChart holding one Bar rather than a
// BarChart. That started as a cursor fix — Cursor.js sends chartName ===
// 'BarChart' down a branch that always builds a Rectangle sized to
// tooltipAxisBandSize, which is 0 on a time axis, and Rectangle.js returns null
// at width 0 — and it is kept because the two panels being the same chart type
// is one less thing that can differ between them.
// ⚠⚠ THE CROSSHAIR IS A ReferenceLine WE DRAW, NOT RECHARTS' CURSOR. Both
// charts set cursor={false} and render a line at the shared hovered X. See the
// crosshair block below for why the library's syncId was abandoned.

import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  ComposedChart, Bar, Scatter, Cell, ReferenceLine,
  XAxis, YAxis, ResponsiveContainer,
} from "recharts";
import { C, s, font, backendFetch } from "./core";

// ─── ET time helpers ─────────────────────────────────────────────────────────
// The X domain is the 9:30–16:00 ET session for the requested date, expressed in
// epoch-ms. Offsets are derived per-instant so DST is handled without a table.

function _etOffsetMs(utcMs) {
  const d = new Date(utcMs);
  const et = new Date(d.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const utc = new Date(d.toLocaleString("en-US", { timeZone: "UTC" }));
  return utc.getTime() - et.getTime();
}

// Epoch-ms for a wall-clock ET time on a YYYY-MM-DD date. NaN on a bad date.
function etEpoch(dateStr, hour, minute) {
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  const naive = Date.parse(`${dateStr}T${hh}:${mm}:00Z`);
  if (!Number.isFinite(naive)) return NaN;
  return naive + _etOffsetMs(naive);
}

// Today / yesterday as YYYY-MM-DD in ET (the session calendar, not the browser's).
function etDateStr(offsetDays = 0) {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const base = new Date(`${get("year")}-${get("month")}-${get("day")}T12:00:00Z`);
  base.setUTCDate(base.getUTCDate() + offsetDays);
  return base.toISOString().slice(0, 10);
}

const _timeFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false,
});
const fmtTime = (ms) => (Number.isFinite(ms) ? _timeFmt.format(new Date(ms)) : "—");
const _timeSecFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
});
// With seconds: a sweep fires several signals inside one minute.
const fmtTimeSec = (ms) => (Number.isFinite(ms) ? _timeSecFmt.format(new Date(ms)) : "—");

const toMs = (v) => {
  if (v == null) return NaN;
  const n = Date.parse(v);
  return Number.isFinite(n) ? n : NaN;
};

// ─── formatting ──────────────────────────────────────────────────────────────
const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(2)}`;
};
const fmtNum = (v, dp = 2) => (Number.isFinite(Number(v)) ? Number(v).toFixed(dp) : "—");
const fmtInt = (v) => (Number.isFinite(Number(v)) ? Number(v).toLocaleString() : "—");

// ─── dark-pool dot styling ───────────────────────────────────────────────────
// Colour by classification (MIDPOINT is the highest-signal read — a negotiated
// block that paid no spread), radius by notional.
const DP_STYLE = {
  MIDPOINT:  { fill: "#FFFFFF", opacity: 1.0 },
  BUYER:     { fill: C.green,   opacity: 1.0 },
  SELLER:    { fill: C.red,     opacity: 1.0 },
  ABOVE_MID: { fill: C.green,   opacity: 0.42 },
  BELOW_MID: { fill: C.red,     opacity: 0.42 },
  UNKNOWN:   { fill: C.textDim, opacity: 0.8 },
};
const dpStyle = (cls) => DP_STYLE[cls] || DP_STYLE.UNKNOWN;

// ⚠⚠ A LADDER WITH FIXED DOLLAR CUTS ENCODED NOTHING ON REAL DATA. The old
// dpRadius returned 7 for anything over $2M, 4.5 over $500k, else 2.5 — and
// measured on NVDA 2026-09-01 all TWENTY prints cleared the top cut, because the
// smallest was $2,178,500. A $500,000,018 block and a $2.18M block rendered as
// identical dots while the legend claimed "dot size = notional".
// ⚠ Shifted-log, normalised against the SET being drawn rather than against
// constants, so the smallest print in any chart is rMin and the largest is rMax
// with no thresholds to re-tune. A $50k print and a $500M print share a chart
// without configuration.
// ⚠ `!(max > min)` rather than `max === min`: it also catches a single print,
// an inverted range, and NaN, all of which would otherwise divide by zero or
// take Math.log of a negative and hand back a NaN radius — a circle that
// silently does not render.
function logScale(value, min, max, rMin, rMax) {
  if (!(max > min)) return (rMin + rMax) / 2;
  const v = Math.min(Math.max(Number(value) || 0, min), max);
  // ⚠⚠ LOG OF THE VALUE, NOT OF THE OFFSET FROM min. The shifted form —
  // log(v - min + 1) / log(max - min + 1) — spends most of its range on the gap
  // between the smallest print and the second smallest. Measured on NVDA
  // 2026-09-01 it put SIX of seven dots between r=10.4 and r=12.0; this spreads
  // the same seven across 3.00 / 6.28 / 6.40 / 7.11 / 7.61 / 11.34 / 12.00.
  // ⚠ Floored at 1 because log(0) is -Infinity and log(negative) is NaN, and
  // `notional` reaches here as `Number(x) || 0` — so a single null print would
  // otherwise make min 0 and hand back a NaN radius for the whole chart, a
  // circle that silently does not render. Dollars have no meaning below 1.
  const lo = Math.max(1, min);
  const logVal = Math.log(Math.max(lo, v)) - Math.log(lo);
  const logMax = Math.log(Math.max(lo + 1, max)) - Math.log(lo);
  return rMin + (logVal / logMax) * (rMax - rMin);
}

const DP_R_MIN = 3;
const DP_R_MAX = 12;

const strengthDots = (n) => "●".repeat(Math.max(0, Math.min(3, Number(n) || 0))) || "—";

// ─── unusual-options bar styling ─────────────────────────────────────────────
const UO_STYLE = {
  EXTREME_CALL: { fill: C.amber,   opacity: 1.0 },
  STRONG_CALL:  { fill: C.green,   opacity: 1.0 },
  UNUSUAL_CALL: { fill: C.green,   opacity: 0.45 },
  EXTREME_PUT:  { fill: "#ff2d2d", opacity: 1.0 },
  STRONG_PUT:   { fill: C.red,     opacity: 1.0 },
  UNUSUAL_PUT:  { fill: C.red,     opacity: 0.45 },
};
const uoStyle = (sig) => UO_STYLE[sig] || { fill: C.textDim, opacity: 0.7 };

// ─── candlestick shape ───────────────────────────────────────────────────────
// Rendered by a Bar bound to the [low, high] range, so Recharts hands us `y` =
// the pixel of `high` and `height` = the pixel span of (high - low). The body is
// derived from that scale — no access to chart internals.
function Candle(props) {
  const { x, y, width, height, payload } = props;
  const o = Number(payload?.o), h = Number(payload?.h);
  const l = Number(payload?.l), c = Number(payload?.c);
  if (![o, h, l, c].every(Number.isFinite)) return null;

  const up = c >= o;
  const color = up ? C.green : C.red;
  const w = Math.max(1, width || 1);
  const cx = x + w / 2;

  // A flat minute (high === low) has zero pixel span — draw a single tick.
  if (!(h > l) || !(height > 0)) {
    return <line x1={x} x2={x + w} y1={y} y2={y} stroke={color} strokeWidth={1} />;
  }

  const scale = height / (h - l);
  const yOpen = y + (h - o) * scale;
  const yClose = y + (h - c) * scale;
  const bodyTop = Math.min(yOpen, yClose);
  const bodyH = Math.max(1, Math.abs(yClose - yOpen));

  return (
    <g>
      <line x1={cx} x2={cx} y1={y} y2={y + height} stroke={color} strokeWidth={1} />
      <rect x={x} y={bodyTop} width={w} height={bodyH} fill={color} />
    </g>
  );
}

// Dark-pool dot — radius by notional, colour by classification.
function DpDot(props) {
  // ⚠ nMin/nMax arrive because Recharts cloneElement's the `shape` element and
  // merges the point props in — the same mechanism that already supplies cx, cy
  // and payload. Neither key can collide with those.
  const { cx, cy, payload, nMin, nMax } = props;
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
  const st = dpStyle(payload?.classification);
  const r = logScale(payload?.notional,
                     Number.isFinite(nMin) ? nMin : 0,
                     Number.isFinite(nMax) ? nMax : 0,
                     DP_R_MIN, DP_R_MAX);
  return (
    <circle
      cx={cx} cy={cy} r={r}
      fill={st.fill} fillOpacity={st.opacity}
      stroke={C.bg} strokeWidth={0.5}
    />
  );
}

// ─── tooltips ────────────────────────────────────────────────────────────────
const TT_BOX = {
  background: "rgba(11,14,20,0.96)",
  border: `1px solid ${C.borderHi}`,
  padding: "8px 10px",
  fontFamily: font,
  fontSize: 11,
  color: C.text,
  lineHeight: 1.6,
  maxWidth: 280,
};
const TTRow = ({ label, value, color }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
    <span style={{ color: C.textDim }}>{label}</span>
    <span style={{ color: color || C.text }}>{value}</span>
  </div>
);

// ── Synced crosshair label ──
// The vertical line both lanes share (syncT) carries its time, so the lane the
// pointer is NOT over still reads "10:42 ET". It flips to the left of the line in
// the right fifth of the session so it never runs off the plot.
function syncLabel(syncT, xDomain) {
  if (!Number.isFinite(syncT)) return null;
  const [a, b] = Array.isArray(xDomain) ? xDomain : [NaN, NaN];
  const frac = Number.isFinite(a) && Number.isFinite(b) && b > a ? (syncT - a) / (b - a) : 0;
  return { value: `${fmtTime(syncT)} ET`, flip: frac > 0.8 };
}

function SyncLabel({ viewBox, value, flip }) {
  if (!viewBox || !value) return null;
  return (
    <text x={viewBox.x + (flip ? -4 : 4)} y={viewBox.y + 10}
          textAnchor={flip ? "end" : "start"} fill={C.text} fontSize={9} fontFamily={font}
          stroke={C.panel} strokeWidth={3} paintOrder="stroke">
      {value}
    </text>
  );
}

// Price card: the candle containing the pointer time (O/H/L/C, volume), then the
// dark pool print within a few pixels of it, when there is one. Same box as the
// options card; every fragment hides when its value is missing.
export function PriceCard({ candle, print, desktop, chartW, style }) {
  if (!candle && !print) return null;
  const st = print ? dpStyle(print.classification) : null;
  const bidAsk = print && real(print.bid) && real(print.ask) ? `bid/ask ${fmtNum(print.bid)} / ${fmtNum(print.ask)}` : null;
  const dots = print && real(print.signal_strength) && print.signal_strength > 0 ? strengthDots(print.signal_strength) : null;
  const trade = print ? [real(print.price) ? fmtNum(print.price) : null,
                         real(print.size) ? `× ${fmtInt(print.size)} sh` : null,
                         real(print.notional) && print.notional > 0 ? `= ${fmtMoney(print.notional)}` : null].filter(Boolean).join(" ") : "";
  return (
    <div data-price-card="1" style={{ ...uoCardBox({ desktop, chartW }), ...style }}>
      {candle && (
        <>
          <div data-line="candle-head" style={{ color: C.textMid }}>{`${fmtTime(candle.t)} ET`}</div>
          <div data-line="ohl">{`O ${fmtNum(candle.o)} · H ${fmtNum(candle.h)} · L ${fmtNum(candle.l)}`}</div>
          <div data-line="close">
            <span style={{ color: candle.c >= candle.o ? C.green : C.red }}>{`C ${fmtNum(candle.c)}`}</span>
            {real(candle.v) ? <span style={{ color: C.textMid }}>{` · vol ${fmtInt(candle.v)}`}</span> : null}
          </div>
        </>
      )}
      {print && (
        <div data-print="1" style={candle ? { borderTop: `1px solid ${C.border}`, marginTop: 3, paddingTop: 3 } : undefined}>
          <div data-line="print-head">
            <span style={{ color: st.fill, fontWeight: 700 }}>{str(print.classification) ? print.classification : "UNKNOWN"}</span>
            {real(print.t) ? <span style={{ color: C.textMid }}>{` · ${fmtTimeSec(print.t)}`}</span> : null}
          </div>
          {trade ? <div data-line="print-trade">{trade}</div> : null}
          {bidAsk || dots ? <div data-line="print-quote" style={{ color: C.textMid }}>{[bidAsk, dots].filter(Boolean).join(" · ")}</div> : null}
        </div>
      )}
    </div>
  );
}

// ─── compact options card ────────────────────────────────────────────────────
// Four lines (five on desktop when OI exists), each fragment only when its value
// is real; a real 0 still shows. fmtMoney/fmtInt print "$0.00" / "0" for null
// (Number(null) is 0), so values are screened before they are formatted.
//   1  10:30:07 · STRONG CALL [SWEEP]
//   2  SPY $610 C · exp Oct 23
//   3  $1.20M on 400 ct · 1,800 vol today
//   4  OTM 1.4% · close IV 18.3% Δ 0.31
//   5  OI 2,500 as of 2026-10-16          (desktop only)
// ⚠ The classifier's note is always "SPY $730P: 49,652 contracts today, $7.9M
// premium on this print" — lines 2 and 3 say the same thing, and line 3 keeps
// its one distinction (volume is the day's running total, premium is this print).
// A note in any OTHER form is kept as a last line, never dropped. "Sweep" or
// "complex/multi-leg" — from the row's own flags or from a note's wording — is a
// chip next to the signal label.
const real = (v) => typeof v === "number" && Number.isFinite(v);
const str = (v) => typeof v === "string" && v.trim().length > 0;
const STD_NOTE = /^\S+ \$\d+(\.\d+)?[CP]: [\d,]+ contracts today, \$\d+(\.\d+)?[KMB]? premium on this print$/;

export const PHONE_MAX_W = 600;
// Phone layout: a narrow screen or a touch-first pointer.
export function isPhoneLayout() {
  if (typeof window === "undefined") return false;
  if (Number.isFinite(window.innerWidth) && window.innerWidth < PHONE_MAX_W) return true;
  try { return !!window.matchMedia?.("(pointer: coarse)")?.matches; } catch { return false; }
}

const _expFmt = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
function fmtExpiry(v) {
  if (!str(v)) return null;
  const ms = Date.parse(`${String(v).slice(0, 10)}T00:00:00Z`);
  return Number.isFinite(ms) ? _expFmt.format(new Date(ms)) : String(v);
}
const fmtStrike = (k) => `$${Number.isInteger(k) ? k : fmtNum(k, 2)}`;

export function uoFlags(d) {
  const note = str(d?.note) ? d.note.trim() : "";
  return {
    sweep: d?.is_sweep === true || /\bsweep/i.test(note),
    complex: d?.is_complex === true || /\b(complex|multi-?leg)/i.test(note),
    extraNote: note && !STD_NOTE.test(note) ? note : null,
  };
}

// The card's lines as plain data — rendered by UoCard, asserted by the harness.
export function uoCardLines(d, { desktop = true } = {}) {
  if (!d) return [];
  const f = uoFlags(d);
  const lines = [];
  lines.push({
    key: "head",
    time: real(d.t) ? fmtTimeSec(d.t) : null,
    label: str(d.signal) ? d.signal.replace(/_/g, " ") : null,
    chips: [f.sweep && "SWEEP", f.complex && "COMPLEX"].filter(Boolean),
  });
  const side = str(d.option_type) ? d.option_type.trim()[0].toUpperCase() : null;
  const exp = fmtExpiry(d.expiry);
  const contract = [str(d.underlying) ? d.underlying : null, real(d.strike) ? fmtStrike(d.strike) : null, side]
    .filter(Boolean).join(" ");
  const l2 = [contract || null, exp ? `exp ${exp}` : null].filter(Boolean);
  if (l2.length) lines.push({ key: "contract", text: l2.join(" · ") });
  // Premium is THIS print's (on its size, in contracts); volume is the day's
  // running total — kept apart in the wording, short enough to stay one line.
  const onPrint = real(d.premium)
    ? `${fmtMoney(d.premium)} on ${real(d.size) ? `${fmtInt(d.size)} ct` : "print"}`
    : (real(d.size) ? `${fmtInt(d.size)} ct` : null);
  const l3 = [onPrint, real(d.volume_today) ? `${fmtInt(d.volume_today)} vol today` : null].filter(Boolean);
  if (l3.length) lines.push({ key: "size", text: l3.join(" · ") });
  const close = [real(d.iv_eod) ? `IV ${fmtNum(d.iv_eod * 100, 1)}%` : null,
                 real(d.delta_eod) ? `Δ ${fmtNum(d.delta_eod, 2)}` : null].filter(Boolean);
  const l4 = [real(d.otm_pct) ? `OTM ${fmtNum(d.otm_pct, 1)}%` : null,
              close.length ? `close ${close.join(" ")}` : null].filter(Boolean);
  if (l4.length) lines.push({ key: "enriched", text: l4.join(" · ") });
  if (desktop && real(d.open_interest)) {
    lines.push({ key: "oi", text: str(d.oi_date)
      ? `OI ${fmtInt(d.open_interest)} as of ${d.oi_date}` : `OI ${fmtInt(d.open_interest)}` });
  }
  if (f.extraNote) lines.push({ key: "note", text: f.extraNote });
  return lines;
}

const CHIP = (c) => ({
  fontSize: "0.85em", fontWeight: 700, color: c, border: `1px solid ${c}88`,
  borderRadius: 2, padding: "0 4px", marginLeft: 6, letterSpacing: "0.04em",
});

export function UoLines({ d, desktop }) {
  const st = uoStyle(d?.signal);
  return uoCardLines(d, { desktop }).map((l) => (
    l.key === "head" ? (
      <div key="head" data-line="head" style={{ whiteSpace: "nowrap" }}>
        {l.time && <span style={{ color: C.textMid }}>{l.time}</span>}
        {l.time && l.label && <span style={{ color: C.textDim }}> · </span>}
        {l.label && <span style={{ color: st.fill, fontWeight: 700 }}>{l.label}</span>}
        {l.chips.map((c) => <span key={c} style={CHIP(C.amber)}>{c}</span>)}
      </div>
    ) : (
      <div key={l.key} data-line={l.key} style={l.key === "note"
        ? { color: C.textMid, fontStyle: "italic", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
        : { color: l.key === "size" ? C.text : C.textMid }}>
        {l.text}
      </div>
    )
  ));
}

// The card box: at most 220px on desktop (10.5px text, so each line fits on one
// row), about 70% of the chart on a phone with 10px text.
export function uoCardBox({ desktop, chartW }) {
  return {
    background: "rgba(11,14,20,0.96)", border: `1px solid ${C.borderHi}`,
    padding: desktop ? "6px 9px" : "5px 7px", fontFamily: font, color: C.text,
    fontSize: desktop ? 10.5 : 10, lineHeight: 1.5, boxSizing: "border-box",
    maxWidth: desktop ? 220 : Math.max(160, Math.round(0.7 * (Number(chartW) || 0))),
  };
}

export function UoCard({ d, desktop, chartW, style }) {
  return (
    <div data-uo-card="1" style={{ ...uoCardBox({ desktop, chartW }), ...style }}>
      <UoLines d={d} desktop={desktop} />
    </div>
  );
}


// ─── options bars grouped by time window ─────────────────────────────────────
// ⚠⚠ ONE BAR PER WINDOW, NEVER ONE PER SIGNAL. A sweep fires several signals in
// the same second and a busy morning fires them every few seconds, while a 4px bar
// covered ~2 minutes of axis on desktop and ~6 on a phone. The bars piled on top
// of each other, the one on top was not the one selected, and same-second signals
// were unreachable. Windows are sized from the plot width so adjacent bars never
// overlap: the smallest step whose pitch is at least UO_MIN_PITCH_PX, the bar one
// pixel narrower than its pitch.
// ⚠ BAR HEIGHT KEEPS ITS MEANING. A signal's bar was its contract's volume_today —
// that contract's running day volume when the print fired. A window's bar is the
// sum, over the DISTINCT contracts that fired in it, of each contract's latest
// volume_today; a contract that printed three times counts once, at its newest
// running total. A one-signal window is exactly the old bar.
export const UO_WINDOWS_MIN = [1, 2, 3, 5, 10, 15, 30];
export const UO_MIN_PITCH_PX = 2.5;
export const PLOT_INSET = 58 + 8;          // YAxis width + right margin, both lanes
export const DEFAULT_LANE_W = 800;         // before the lane has been measured

export function uoWindowMinutes(plotW, spanMs) {
  const minutes = spanMs / 60000;
  if (!(plotW > 0) || !(minutes > 0)) return UO_WINDOWS_MIN[0];
  const pxPerMin = plotW / minutes;
  return UO_WINDOWS_MIN.find((w) => w * pxPerMin >= UO_MIN_PITCH_PX) ?? UO_WINDOWS_MIN[UO_WINDOWS_MIN.length - 1];
}

const UO_TIER = (sig) => (/^EXTREME/.test(sig) ? 3 : /^STRONG/.test(sig) ? 2 : /^UNUSUAL/.test(sig) ? 1 : 0);
const contractKey = (s) => (str(s.option_symbol) ? s.option_symbol
  : `${s.underlying}|${s.strike}|${s.option_type}|${s.expiry}`);
const premiumOf = (s) => (real(s.premium) ? s.premium : -1);

// options must carry `t` (epoch ms) and `vol`; anchor = session open, so windows
// start on round times.
export function groupOptions(options, anchor, windowMs) {
  const by = new globalThis.Map();
  for (const s of options || []) {
    if (!real(s?.t)) continue;
    const k = Math.floor((s.t - anchor) / windowMs);
    if (!by.has(k)) by.set(k, []);
    by.get(k).push(s);
  }
  return [...by.entries()].sort((a, b) => a[0] - b[0]).map(([k, list]) => {
    const start = anchor + k * windowMs;
    const latest = new globalThis.Map();            // contract -> newest signal
    for (const s of list) {
      const key = contractKey(s);
      const cur = latest.get(key);
      if (!cur || s.t >= cur.t) latest.set(key, s);
    }
    let vol = 0;
    for (const s of latest.values()) vol += Number(s.vol) || 0;
    const signals = [...list].sort((a, b) => premiumOf(b) - premiumOf(a) || a.t - b.t);
    // Colour: the strongest tier in the window, ties to the larger premium.
    const lead = [...signals].sort((a, b) => UO_TIER(b.signal) - UO_TIER(a.signal))[0];
    return { t: start + windowMs / 2, start, end: start + windowMs, windowMs, n: list.length,
             vol, signals, signal: lead?.signal, key: `w${k}` };
  });
}

// X-axis ticks on round session times (the axis used to place a tick at every
// data timestamp — duplicate times gave duplicate React keys).
export function sessionTicks(xDomain, anchor, plotW) {
  const [lo, hi] = xDomain || [];
  if (!real(lo) || !real(hi) || !(hi > lo)) return undefined;
  const step = (plotW < 400 ? 120 : plotW < 700 ? 60 : 30) * 60000;
  const out = [];
  for (let x = anchor + Math.ceil((lo - anchor) / step) * step; x <= hi; x += step) out.push(x);
  return out;
}

// Card for one window. A one-signal window is the single-signal card. A group is
// a SUMMARY of at most five rows — it used to repeat the four-line card for each
// of its top three signals (~15 lines, taller than the tooltip it replaced):
//   16 signals · 10:30–10:35 · 41K vol
//   10:30:00  $605 C  $900K  EXTREME
//   10:30:00  $610 C  $500K  STRONG
//   10:30:00  $600 C  $300K  STRONG
//   +13 more
// Rows are the top three by premium. Every row is one line (nowrap, ellipsis), so
// the card can never grow past five rows; each fragment hides when missing.
const fmtQty = (v) => (!real(v) ? null : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e4 ? `${Math.round(v / 1e3)}K`
  : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : String(Math.round(v)));
const ROW1 = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

export function uoGroupRows(g) {
  const span = g.windowMs > 60000 ? `${fmtTime(g.start)}–${fmtTime(g.end)}` : fmtTime(g.start);
  const vol = fmtQty(g.vol);
  const head = [`${g.n} signals`, span, vol ? `${vol} vol` : null].filter(Boolean).join(" · ");
  const rows = g.signals.slice(0, 3).map((s) => {
    const side = str(s.option_type) ? s.option_type.trim()[0].toUpperCase() : null;
    const contract = [real(s.strike) ? fmtStrike(s.strike) : null, side].filter(Boolean).join(" ");
    return {
      time: real(s.t) ? fmtTimeSec(s.t) : null,
      contract: contract || null,
      premium: real(s.premium) ? fmtMoney(s.premium) : null,
      tier: str(s.signal) ? s.signal.split("_")[0] : null,
      signal: s.signal,
    };
  });
  return { head, rows, more: g.n > 3 ? `+${g.n - 3} more` : null };
}

export function UoGroupCard({ g, desktop, chartW, style }) {
  if (!g || !g.n) return null;
  if (g.n === 1) return <UoCard d={g.signals[0]} desktop={desktop} chartW={chartW} style={style} />;
  const { head, rows, more } = uoGroupRows(g);
  return (
    <div data-uo-card="group" style={{ ...uoCardBox({ desktop, chartW }), ...style }}>
      <div data-line="group-head" style={{ ...ROW1, color: C.text, fontWeight: 700 }}>{head}</div>
      {rows.map((r, i) => (
        <div key={i} data-line="row" data-entry="1" style={ROW1}>
          {[r.time && ["t", r.time, { color: C.textMid }],
            r.contract && ["c", r.contract, {}],
            r.premium && ["p", r.premium, { color: C.amber }],
            r.tier && ["s", r.tier, { color: uoStyle(r.signal).fill, fontWeight: 700 }]]
            .filter(Boolean)
            .map(([k, v, st], j) => <span key={k} style={{ ...st, marginLeft: j ? 8 : 0 }}>{v}</span>)}
        </div>
      ))}
      {more && <div data-line="more" style={{ ...ROW1, color: C.textDim }}>{more}</div>}
    </div>
  );
}

// ─── explicit pointer selection ──────────────────────────────────────────────
// ⚠⚠ THE POINTER, NOT RECHARTS' "NEAREST POINT". recharts picked the data value
// nearest the pointer from each lane's OWN list — and when any child has its own
// `data`, that list is ONLY that child's: the price lane snapped to dark pool
// PRINTS (never a candle; stuck on one spot on a one-print day), the options lane
// to the nearest signal even across empty space. The time under the pointer is
// now computed from its x, both lanes share it, and each lane picks its own item
// from it.
export const PRINT_TOL_PX = { mouse: 6, touch: 10 };
export const BAR_TOL_PX = { mouse: 3, touch: 8 };

// x (px, within the lane) → epoch ms, or null outside the plot area.
export function pointerTime(x, laneW, xDomain) {
  const [d0, d1] = xDomain || [];
  const plotL = 58, plotR = laneW - 8;
  if (!(plotR > plotL) || !real(d0) || !real(d1) || !(x >= plotL && x <= plotR)) return null;
  return d0 + ((x - plotL) / (plotR - plotL)) * (d1 - d0);
}

// The candle whose interval [t, t + step) contains the time.
export function candleAt(bars, t) {
  if (!bars?.length || !real(t)) return null;
  let lo = 0, hi = bars.length - 1;
  if (t < bars[0].t) return null;
  while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (bars[mid].t <= t) lo = mid; else hi = mid - 1; }
  const gaps = [];
  for (let i = 1; i < Math.min(bars.length, 40); i++) gaps.push(bars[i].t - bars[i - 1].t);
  gaps.sort((a, b) => a - b);
  const step = gaps.length ? gaps[gaps.length >> 1] : 60000;
  return t < bars[lo].t + step ? bars[lo] : null;
}

// Nearest item to the time within a tolerance (ms), or null.
export function nearestWithin(items, t, tolMs) {
  let best = null, bd = Infinity;
  for (const it of items || []) {
    const d = Math.abs(it.t - t);
    if (d <= tolMs && d < bd) { best = it; bd = d; }
  }
  return best;
}

export function selectAt({ t, laneW, touch, xDomain, bars, prints, groups, barPx }) {
  if (!real(t) || !(laneW > 0)) return null;
  const span = (xDomain?.[1] ?? 0) - (xDomain?.[0] ?? 0);
  const pxPerMs = Math.max(1, laneW - PLOT_INSET) / Math.max(1, span);
  const kind = touch ? "touch" : "mouse";
  return {
    candle: candleAt(bars, t),
    print: nearestWithin(prints, t, PRINT_TOL_PX[kind] / pxPerMs),
    group: nearestWithin(groups, t, ((barPx || 1) / 2 + BAR_TOL_PX[kind]) / pxPerMs),
  };
}

// Card placement inside a lane. Desktop: beside the pointer, flipped and clamped
// to stay inside the lane. Phone: never under the finger — the price card at the
// top of its lane, the options card ABOVE its lane, each on the side away from
// the finger.
export const CARD_W_DESKTOP = 220;
export function cardPlacement({ lane, x, laneW, desktop }) {
  if (desktop) {
    const right = x + 14;
    const left = right + CARD_W_DESKTOP <= laneW - 4 ? right : Math.max(4, x - 14 - CARD_W_DESKTOP);
    // top 24: below the crosshair's time label, which sits at the top of the line.
    return { position: "absolute", top: 24, left };
  }
  const side = x < laneW / 2 ? { right: 4 } : { left: 4 };
  return lane === "options"
    ? { position: "absolute", bottom: "calc(100% + 6px)", ...side }
    : { position: "absolute", top: 4, ...side };
}

// ─── crosshair ───────────────────────────────────────────────────────
// ⚠⚠ DRAWN BY US, NOT BY RECHARTS. recharts' syncId was tried and abandoned
// earlier, and its tooltips are gone too. The likely reason sync never crossed:
// when a child (the dark pool Scatter) has its own `data`, recharts' hover list is
// ONLY that child's data — the price chart's tooltip ticks were the prints, not
// its 391 bars, so "nearest point" never meant the candle under the pointer.
// ⚠ What replaces it: each lane's wrapper listens to pointer events, the time
// under the pointer is computed from its x on the shared plot geometry
// (pointerTime), that one value lives in syncT, each lane draws a ReferenceLine
// at it, and selectAt picks the candle, print and options window from it. Both
// cards are ours (PriceCard, UoGroupCard), placed by cardPlacement.

// ─── shared chart furniture ──────────────────────────────────────────────────
const AXIS = { stroke: C.textDim, fontSize: 9, fontFamily: font };
const Msg = ({ children, color }) => (
  <div style={{
    padding: "28px 12px", textAlign: "center", fontSize: 12,
    color: color || C.textDim, lineHeight: 1.7,
  }}>
    {children}
  </div>
);

// ─── main ────────────────────────────────────────────────────────────────────
export function ReplayTab({ keys }) {
  const [ticker, setTicker] = useState("");
  const [dateStr, setDateStr] = useState(() => etDateStr(-1)); // yesterday — today is incomplete
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(null); // {symbol, date} actually rendered
  // The instant under the pointer or finger, epoch-ms, shared by both lanes and
  // computed from the pointer's x (see pointerTime). null when on neither.
  const [syncT, setSyncT] = useState(null);
  // Width of the options lane, measured: the grouping window comes from it.
  const [laneW, setLaneW] = useState(0);
  const laneObs = useRef(null);
  const laneRef = useCallback((el) => {
    laneObs.current?.disconnect();
    laneObs.current = null;
    if (!el) return;
    const read = () => { const w = el.clientWidth; if (w > 0) setLaneW((p) => (Math.abs(p - w) >= 1 ? w : p)); };
    read();
    if (typeof ResizeObserver !== "undefined") {
      laneObs.current = new ResizeObserver(read);
      laneObs.current.observe(el);
    }
  }, []);

  // Where the pointer is (lane-relative x, lane width, touch or not); set with
  // syncT, cleared with it.
  const [pointer, setPointer] = useState(null);
  const clearPointer = useCallback(() => { setSyncT(null); setPointer(null); }, []);

  const load = useCallback(async () => {
    const sym = ticker.trim().toUpperCase();
    if (!sym || !dateStr) return;
    setLoading(true);
    setError("");
    try {
      const path = `/api/replay/${encodeURIComponent(sym)}?date=${encodeURIComponent(dateStr)}`;
      const res = await backendFetch(path, path);
      if (!res.ok) throw new Error(String(res.status));
      const json = await res.json();
      setData(json && typeof json === "object" ? json : null);
      setLoaded({ symbol: sym, date: dateStr });
    } catch {
      setData(null);
      setLoaded({ symbol: sym, date: dateStr });
      setError("Could not load replay data. Try again.");
    }
    setLoading(false);
  }, [ticker, dateStr]);

  // ── normalize the three layers onto one epoch-ms timeline ──
  const view = useMemo(() => {
    const day = loaded?.date || dateStr;
    const open = etEpoch(day, 9, 30);
    const close = etEpoch(day, 16, 0);

    const bars = (Array.isArray(data?.price_bars) ? data.price_bars : [])
      .map((b) => {
        const t = toMs(b?.t);
        const o = Number(b?.o), h = Number(b?.h), l = Number(b?.l), c = Number(b?.c);
        if (!Number.isFinite(t) || ![o, h, l, c].every(Number.isFinite)) return null;
        return { _kind: "bar", t, o, h, l, c, v: Number(b?.v) || 0, range: [l, h] };
      })
      .filter(Boolean)
      .sort((a, b) => a.t - b.t);

    const prints = (Array.isArray(data?.dark_prints) ? data.dark_prints : [])
      .map((p) => {
        const t = toMs(p?.timestamp);
        const price = Number(p?.price);
        if (!Number.isFinite(t) || !Number.isFinite(price)) return null;
        return {
          ...p, _kind: "print", t, price,
          notional: Number(p?.notional) || 0,
          bid: p?.bid == null ? null : Number(p.bid),
          ask: p?.ask == null ? null : Number(p.ask),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.t - b.t);

    // ⚠⚠ VOLUME IS THE ONLY METRIC. There is no ratio mode any more.
    // volume_oi_ratio is NULL on 96.9% of unusual_options_history — 10,996 of
    // 354,708 rows, ALL of them on 2026-07-27, the single day before the stream
    // classifier dropped open interest. It is not an intermittent gap: the
    // classifier makes no REST call by design, OI is per-contract reference data
    // that only a REST call can supply, and nothing has written the column since.
    // A mode switch on a field that will never again be populated is a branch
    // that can only ever take one path.
    const rawOptions = Array.isArray(data?.unusual_options) ? data.unusual_options : [];

    const options = rawOptions
      .map((u) => {
        const t = toMs(u?.timestamp);
        if (!Number.isFinite(t)) return null;
        // ⚠ `vol` is derived rather than leaning on the spread: volume_today
        // arrives as a number today, but a bar bound to a string or a null
        // renders nothing and looks exactly like the zero-height bug this
        // change exists to fix.
        // ⚠ `vol` is derived rather than leaning on the spread: volume_today
        // arrives as a number today, but a bar bound to a string or a null
        // renders nothing and looks exactly like the zero-height bug this lane
        // had when it was keyed on the ratio.
        return { ...u, t, vol: Number(u?.volume_today) || 0 };
      })
      .filter(Boolean)
      .sort((a, b) => a.t - b.t);

    // X domain: the ET session, widened to cover anything stamped outside it
    // (extended-hours prints) so no record is silently clipped off-chart.
    const stamps = [
      ...bars.map((b) => b.t), ...prints.map((p) => p.t), ...options.map((o) => o.t),
    ].filter(Number.isFinite);
    const lo = Number.isFinite(open) ? open : (stamps.length ? Math.min(...stamps) : 0);
    const hi = Number.isFinite(close) ? close : (stamps.length ? Math.max(...stamps) : 1);
    const xDomain = stamps.length
      ? [Math.min(lo, ...stamps), Math.max(hi, ...stamps)]
      : [lo, hi];

    // Y domain covers candles AND print prices, with a small pad.
    const prices = [
      ...bars.flatMap((b) => [b.l, b.h]), ...prints.map((p) => p.price),
    ].filter(Number.isFinite);
    let yDomain = ["auto", "auto"];
    if (prices.length) {
      const min = Math.min(...prices), max = Math.max(...prices);
      const pad = (max - min) * 0.04 || Math.max(0.01, max * 0.001);
      yDomain = [min - pad, max + pad];
    }

    // Notional range for the dot scale, over the prints ACTUALLY drawn.
    const notionals = prints.map((p) => p.notional).filter(Number.isFinite);
    const nMin = notionals.length ? Math.min(...notionals) : 0;
    const nMax = notionals.length ? Math.max(...notionals) : 0;

    const maxVol = options.reduce((m, o) => Math.max(m, Number(o.vol) || 0), 0);
    return { bars, prints, options, xDomain, yDomain, nMin, nMax, maxVol };
  }, [data, loaded, dateStr]);

  const { bars, prints, options, xDomain, yDomain, nMin, nMax } = view;
  const plotW = Math.max(50, (laneW || DEFAULT_LANE_W) - PLOT_INSET);
  const anchor = etEpoch(loaded?.date || dateStr, 9, 30);
  const uoWindowMs = uoWindowMinutes(plotW, xDomain[1] - xDomain[0]) * 60000;
  const uoGroups = useMemo(() => groupOptions(options, Number.isFinite(anchor) ? anchor : xDomain[0], uoWindowMs),
    [options, anchor, xDomain, uoWindowMs]);
  const uoPitch = uoWindowMs / Math.max(1, xDomain[1] - xDomain[0]) * plotW;
  const uoBar = Math.max(1, Math.round((uoPitch - 1) * 10) / 10);
  const maxVol = uoGroups.reduce((m, g) => Math.max(m, g.vol), 0);
  const ticks = sessionTicks(xDomain, Number.isFinite(anchor) ? anchor : xDomain[0], plotW);
  const syncLbl = syncLabel(syncT, xDomain);

  // ⚠ Pointer events cover mouse, pen and touch in one path.
  // ⚠⚠ A TOUCH PINS; A MOUSE FOLLOWS. With a mouse the line and cards follow the
  // pointer and leave with it. With a finger they stay where the finger LIFTED:
  // lifting, the pointerleave the browser fires right after a lift, and a
  // pointercancel (the page taking the gesture to scroll) all leave the
  // selection in place. Touching either lane again moves it; a tap anywhere
  // outside both lanes dismisses it. A touch is captured by the lane it started
  // on, so moves keep arriving after the finger slides off it — those keep the
  // last point that was on the lane instead of clearing.
  const onLanePointer = useCallback((lane) => (e) => {
    const touch = e.pointerType === "touch";
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const w = r.width || el.clientWidth || 0;
    const h = r.height || el.clientHeight || 0;
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const off = !(w > 0) || x < 0 || y < 0 || x > w || (h > 0 && y > h);
    const tm = off ? null : pointerTime(x, w, xDomain);
    if (tm === null) { if (!touch) clearPointer(); return; }
    setSyncT(tm);
    setPointer({ lane, x, w, touch });
  }, [xDomain, clearPointer]);
  const onLaneEnd = useCallback((e) => { if (e.pointerType !== "touch") clearPointer(); }, [clearPointer]);
  const laneEvents = (lane) => ({
    "data-lane": lane,
    onPointerMove: onLanePointer(lane), onPointerDown: onLanePointer(lane),
    onPointerLeave: onLaneEnd, onPointerCancel: onLaneEnd,
  });
  // Dismiss a pinned (touch) selection with a tap anywhere outside both lanes.
  // Capture phase, so it runs before anything on the page handles the tap; a
  // tap ON a lane is left to the lane, which moves the selection instead.
  const touchPinned = !!pointer?.touch;
  useEffect(() => {
    if (!touchPinned) return undefined;
    const onDown = (e) => {
      const lane = e.target?.closest?.("[data-lane]");
      if (!lane) clearPointer();
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [touchPinned, clearPointer]);
  const desktop = !isPhoneLayout();
  const sel = useMemo(() => (pointer ? selectAt({
    t: syncT, laneW: pointer.w, touch: pointer.touch, xDomain, bars, prints, groups: uoGroups, barPx: uoBar,
  }) : null), [syncT, pointer, xDomain, bars, prints, uoGroups, uoBar]);
  const hasAny = bars.length > 0 || prints.length > 0 || options.length > 0;
  const label = loaded ? `${loaded.symbol} on ${loaded.date}` : "";

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ marginBottom: 12 }}>
        <div style={{
          fontSize: 16, fontWeight: 700, color: C.amber, letterSpacing: "0.08em",
        }}>
          DARK POOL REPLAY
        </div>
        <div style={{ fontSize: 11, color: C.textDim, marginTop: 3 }}>
          Price action + institutional flow overlay
        </div>
      </div>

      {/* ── Controls ── */}
      <div style={s.panel}>
        <div style={{ ...s.panelB, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={ticker}
            onChange={(e) => setTicker(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Ticker…"
            style={{
              width: 130, minHeight: 44, boxSizing: "border-box",
              background: C.panelAlt, border: `1px solid ${C.border}`, color: C.text,
              fontFamily: font, fontSize: 13, padding: "8px 10px", borderRadius: 3,
            }}
          />
          <input
            type="date"
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            style={{
              minHeight: 44, boxSizing: "border-box",
              background: C.panelAlt, border: `1px solid ${C.border}`, color: C.text,
              fontFamily: font, fontSize: 13, padding: "8px 10px", borderRadius: 3,
            }}
          />
          <button
            onClick={load}
            disabled={loading || !ticker.trim()}
            style={{
              ...s.btn("amber"), minHeight: 44, padding: "8px 14px", fontSize: 11,
              opacity: loading || !ticker.trim() ? 0.5 : 1,
            }}
          >
            {loading ? "LOADING…" : "LOAD REPLAY"}
          </button>
          {error ? (
            <span style={{ fontSize: 11, color: C.red }}>{error}</span>
          ) : null}
        </div>
      </div>

      {/* ── Price panel ── */}
      <div style={{ ...s.panel, marginTop: 10 }}>
        <div style={s.panelH}>
          <span style={s.panelT}>
            PRICE + DARK POOL PRINTS{label ? ` · ${label}` : ""}
          </span>
          {prints.length ? (
            <span style={{ fontSize: 9, color: C.textDim }}>
              {prints.length} print{prints.length === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>
        <div style={s.panelB}>
          {!loaded && !loading ? (
            <Msg>Select a ticker and date to load the replay</Msg>
          ) : loading ? (
            <Msg color={C.amber}>Loading replay data…</Msg>
          ) : !hasAny ? (
            <Msg>No replay data available for {label}</Msg>
          ) : (
            <>
              {bars.length === 0 ? (
                <Msg color={C.textMid}>Price data unavailable for {loaded?.date}</Msg>
              ) : null}
              {(bars.length > 0 || prints.length > 0) && (
                <div {...laneEvents("price")}
                     style={{ position: "relative", width: "100%", height: 340, touchAction: "pan-y" }}>
                  <ResponsiveContainer>
                    <ComposedChart data={bars}
                                   margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                      <XAxis
                        dataKey="t" type="number" domain={xDomain} scale="time"
                        ticks={ticks} tickFormatter={fmtTime} {...AXIS}
                      />
                      <YAxis
                        domain={yDomain} tickFormatter={(v) => fmtNum(v, 2)}
                        width={58} {...AXIS}
                      />
                      {Number.isFinite(syncT) && (
                        <ReferenceLine x={syncT} stroke={C.borderHi} strokeWidth={1}
                                       label={<SyncLabel {...syncLbl} />} />
                      )}
                      <Bar dataKey="range" shape={<Candle />} barSize={3} isAnimationActive={false} />
                      <Scatter
                        data={prints} dataKey="price"
                        shape={<DpDot nMin={nMin} nMax={nMax} />}
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                  {sel && (sel.candle || sel.print) && (
                    <PriceCard candle={sel.candle} print={sel.print} desktop={desktop} chartW={pointer.w}
                               style={{ ...cardPlacement({ lane: "price", x: pointer.x, laneW: pointer.w, desktop }),
                                        zIndex: 6, pointerEvents: "none" }} />
                  )}
                </div>
              )}
              {prints.length === 0 ? (
                <div style={{ fontSize: 11, color: C.textDim, padding: "8px 2px", lineHeight: 1.6 }}>
                  No dark pool prints recorded for {label}. Collection may have been
                  inactive, or no print met the significance threshold.
                </div>
              ) : (
                <div style={{
                  display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6,
                  fontSize: 9, color: C.textDim,
                }}>
                  {Object.keys(DP_STYLE).map((k) => (
                    <span key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{
                        width: 7, height: 7, borderRadius: "50%",
                        background: DP_STYLE[k].fill, opacity: DP_STYLE[k].opacity,
                        display: "inline-block",
                      }} />
                      {k}
                    </span>
                  ))}
                  <span>· dot size = notional</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Options activity lane ── */}
      <div style={{ ...s.panel, marginTop: 10 }}>
        <div style={s.panelH}>
          <span style={s.panelT}>UNUSUAL OPTIONS ACTIVITY</span>
          {options.length ? (
            <span style={{ fontSize: 9, color: C.textDim }}>
              {options.length} signal{options.length === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>
        <div style={s.panelB}>
          {!loaded && !loading ? (
            <Msg>Select a ticker and date to load the replay</Msg>
          ) : loading ? (
            <Msg color={C.amber}>Loading replay data…</Msg>
          ) : options.length === 0 ? (
            <Msg>No unusual options signals recorded for {label}</Msg>
          ) : (
            <>
              <div ref={laneRef} {...laneEvents("options")}
                   style={{ position: "relative", width: "100%", height: 150, touchAction: "pan-y" }}>
                <ResponsiveContainer>
                  <ComposedChart data={uoGroups}
                                 margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                    <XAxis
                      dataKey="t" type="number" domain={xDomain} scale="time"
                      ticks={ticks} tickFormatter={fmtTime} {...AXIS}
                    />
                    <YAxis
                      domain={[0, Math.max(1, Math.ceil(maxVol * 1.1))]}
                      tickFormatter={(v) => fmtInt(v)}
                      width={58} {...AXIS}
                    />
                    {Number.isFinite(syncT) && (
                      <ReferenceLine x={syncT} stroke={C.borderHi} strokeWidth={1}
                                     label={<SyncLabel {...syncLbl} />} />
                    )}
                    <Bar dataKey="vol" barSize={uoBar} isAnimationActive={false}>
                      {uoGroups.map((g) => {
                        const st = uoStyle(g.signal);
                        return <Cell key={g.key} fill={st.fill} fillOpacity={st.opacity} />;
                      })}
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
                {sel?.group && (
                  <UoGroupCard g={sel.group} desktop={desktop} chartW={pointer.w}
                               style={{ ...cardPlacement({ lane: "options", x: pointer.x, laneW: pointer.w, desktop }),
                                        zIndex: 6, pointerEvents: "none" }} />
                )}
              </div>
              <div style={{
                display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6,
                fontSize: 9, color: C.textDim,
              }}>
                {Object.keys(UO_STYLE).map((k) => (
                  <span key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{
                      width: 7, height: 7, background: UO_STYLE[k].fill,
                      opacity: UO_STYLE[k].opacity, display: "inline-block",
                    }} />
                    {k}
                  </span>
                ))}
                {/* ⚠ One label, unconditional, and it is the thing actually
                    drawn. The old "volume / open interest" text over a lane of
                    zero-height bars is how a collection gap came to look like a
                    rendering bug. */}
                <span>· bar height = volume</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ⚠ TEST-ONLY named export, read by harness/uo_render.mjs (hide-when-null tooltip
// checks). The app imports only ReplayTab.
export const __replayTest = { SyncLabel, syncLabel, UoCard, uoCardLines, uoFlags, uoCardBox, PriceCard,
  pointerTime, candleAt, nearestWithin, selectAt, cardPlacement,
  UoGroupCard, uoGroupRows, groupOptions, uoWindowMinutes, sessionTicks };

export default ReplayTab;
